/**
 * lenis.js — Shared Lenis Smooth Scroll Manager
 *
 * Single owner of the global Lenis instance (window.lenis) and its
 * cross-document lifecycle. Other code should listen for the
 * "wallop-motion:lenis-ready" event (or call getLenis()) rather than
 * creating its own instance.
 *
 * Lenis is initialised if:
 *   - The <body> has [data-smooth-scroll] (site-wide opt-in), OR
 *   - A module explicitly calls ensureLenis()
 *
 * The opt-in check is handled by initLenis(), called from main.js.
 *
 * prefers-reduced-motion is handled by Lenis itself (respectReducedMotion,
 * on by default): it disables smoothing while continuing to run, which
 * keeps DOM sync intact and window.lenis available. Do not add a manual
 * bail-out here — it would leave window.lenis null and break anything
 * depending on the instance existing.
 */

const SMOOTH_SCROLL_ATTR = "data-smooth-scroll";

/**
 * Create or reuse a global Lenis instance.
 * Returns null if Lenis is not available.
 */
export function ensureLenis() {
  if (typeof Lenis === "undefined") return null;
  if (window.lenis) return window.lenis;

  const lenis = new Lenis({
    autoRaf: true,
    anchors: true,
    autoToggle: true,
    allowNestedScroll: true,
  });
  window.lenis = lenis;

  // Public contract for theme code (e.g. GSAP ScrollTrigger wiring) — fires
  // on every creation, including BFCache re-init, so consumers don't have
  // to poll window.lenis or depend on enqueue order.
  window.dispatchEvent(
    new CustomEvent("wallop-motion:lenis-ready", { detail: { lenis } })
  );

  return lenis;
}

/**
 * Get the existing Lenis instance (if any).
 * Does not create one — use ensureLenis() for that.
 */
export function getLenis() {
  return window.lenis || null;
}

/**
 * Initialise Lenis if the page has opted in via [data-smooth-scroll] on <body>.
 * Called from main.js on DOMContentLoaded.
 */
export function initLenis() {
  if (!document.body.hasAttribute(SMOOTH_SCROLL_ATTR)) return;

  ensureLenis();
}

/**
 * Lifecycle handling for cross-document navigation.
 * Called once from main.js on DOMContentLoaded.
 */
export function initLenisLifecycle() {
  // Destroy before the browser captures a view-transition snapshot, so the
  // rAF loop can't shift scroll position mid-capture.
  window.addEventListener("pageswap", () => {
    const lenis = getLenis();
    if (!lenis) return;
    lenis.destroy();
    window.lenis = null;
  });

  // BFCache restore: DOMContentLoaded does not fire again, so re-init here.
  // initLenis() re-checks the opt-in attribute, so restore correctly does
  // nothing on pages that never had smooth scroll.
  window.addEventListener("pageshow", (event) => {
    if (!event.persisted) return;
    initLenis();
  });
}
